import React, { useState, useEffect } from 'react';
import { Nav, Tooltip, OverlayTrigger, Offcanvas, Button, Collapse } from 'react-bootstrap';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/darkmode.css';
import '../styles/style.css';
import NotificationDropdown from './NotificationDropdown';
import AppNavbar from './Navbar';
import AiBriefingPopup from './AiBriefingPopup';
import NavGuard from './NavGuard';
import { useTranslation } from 'react-i18next';
import { ButtonGroup } from 'react-bootstrap';

import logoDark from '../assets/logo/logo-dark-bg.png'
import logoWhite from '../assets/logo/logo-white-bg.png'

import logoIconDark from '../assets/logo/logo-icon-dark.png'
import logoIconWhite from '../assets/logo/logo-icon-white.png'

const Layout = () => {
	const { user, navigation, permissions, logout } = useAuth();
	const location = useLocation();
	const { t, i18n } = useTranslation(['menu', 'common']);
	const [darkMode, setDarkMode] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [showAiPopup, setShowAiPopup] = useState(false);
	const isOmnichannel = location.pathname.startsWith('/omnichannel');

	const toggleAiPopup = () => setShowAiPopup(!showAiPopup);

	const [expandedGroups, setExpandedGroups] = useState(() => {
		try {
			const saved = localStorage.getItem('sidebarGroups');
			return saved ? JSON.parse(saved) : {};
		} catch { return {}; }
	});

	// menuData มาจาก navigation (grouped) ใน AuthContext — ไม่เรียก API ซ้ำ
	const menuData = Array.isArray(navigation) ? navigation : [];

	// Auto-expand group containing active route
	useEffect(() => {
		if (menuData.length > 0) {
			const initialExpanded = { ...expandedGroups };
			let changed = false;
			menuData.forEach((group, idx) => {
				const groupId = group.id || group.title || idx;
				const hasActiveItem = group.items.some(item => {
					const targetPath = item.path || item.to;
					return location.pathname.startsWith(targetPath) ||
						(targetPath === '/dashboard' && location.pathname === '/');
				});
				if (hasActiveItem && !initialExpanded[groupId]) {
					initialExpanded[groupId] = true;
					changed = true;
				}
			});
			if (changed) {
				setExpandedGroups(initialExpanded);
			}
		}
		// eslint-disable-next-line
	}, [menuData, location.pathname]);

	const toggleGroup = (groupId) => {
		setExpandedGroups(prev => {
			const next = { ...prev, [groupId]: prev[groupId] === undefined ? false : !prev[groupId] };
			try { localStorage.setItem('sidebarGroups', JSON.stringify(next)); } catch { }
			return next;
		});
	};

	// Load preferences
	useEffect(() => {
		const savedDarkMode = localStorage.getItem('darkMode') === 'true';
		setDarkMode(savedDarkMode);
		applyTheme(savedDarkMode);

		const savedCollapse = localStorage.getItem('sidebarCollapsed') === 'true';
		setIsCollapsed(savedCollapse);
	}, []);


	const applyTheme = (isDark) => {
		const body = document.body;

		if (isDark) {
			body.setAttribute('data-bs-theme', 'dark');
			body.classList.add('dark-mode');
		} else {
			body.setAttribute('data-bs-theme', 'light');
			body.classList.remove('dark-mode');
		}

		// Dynamically update mobile browser theme color (PWA/Safari/Chrome)
		const metaThemeColor = document.querySelector('meta[name="theme-color"]');
		if (metaThemeColor) {
			// #1e2328 matches the dark mode background, #ffffff matches light mode
			metaThemeColor.setAttribute('content', isDark ? '#1e2328' : '#ffffff');
		}
	};

	const toggleDarkMode = () => {
		const newDarkMode = !darkMode;
		setDarkMode(newDarkMode);
		localStorage.setItem('darkMode', newDarkMode.toString());
		applyTheme(newDarkMode);
	};

	const toggleSidebar = () => {
		const newState = !isCollapsed;
		setIsCollapsed(newState);
		localStorage.setItem('sidebarCollapsed', newState.toString());
	};

	const renderTooltip = (text) => (props) => (
		<Tooltip id="menu-tooltip" {...props}>
			{text}
		</Tooltip>
	);

	const toggleMobileMenu = () => {
		setShowMobileMenu(!showMobileMenu);
	};

	const closeMobileMenu = () => {
		setShowMobileMenu(false);
	};

	const filteredMenuGroups = menuData;

	// Sidebar Content Component
	const SidebarContent = ({ isMobile = false }) => (
		<>
			<div className={`navbar-brand-wrapper  mb-4 ${!isMobile && isCollapsed ? 'text-center' : 'ps-2'}`}>
				<div className="d-flex align-items-center justify-content-between">
					<Link to="/" className="text-decoration-none overflow-hidden">
						<div className={`d-flex align-items-center gap-3 ${!isMobile && isCollapsed ? 'justify-content-center' : ''}`}>
							<div className="logo-container d-flex align-items-center justify-content-center">
								<img
									className="navbar-brand-logo"
									src={(!isMobile && isCollapsed)
										? (darkMode ? logoIconWhite : logoIconDark)
										: (darkMode ? logoDark : logoWhite)}
									alt="CRM Logo"
									style={{
										maxHeight: isMobile ? '35px' : (isCollapsed && !isMobile ? '35px' : '40px'),
										maxWidth: isCollapsed && !isMobile ? '35px' : '100%',
										objectFit: 'contain',
										transition: 'all 0.3s'
									}}
								/>
							</div>

							{(!isCollapsed || isMobile) && (
								<div className="brand-info d-flex flex-column justify-content-center animate-fade-in">
									<h6 className={`mb-0 fw-bold ${isMobile ? 'fs-6' : ''}`} style={{ letterSpacing: '-0.5px' }}>
										{/* CRM System */}
									</h6>
									<small className={`${darkMode ? 'text-light-emphasis' : 'text-secondary'} small fw-medium`} style={{ fontSize: '0.75rem' }}>
										Enterprise {process.env.REACT_APP_VERSION || 'v1.0.0'}
									</small>
								</div>
							)}
						</div>
					</Link>

					{!isMobile && !isCollapsed && (
						<button
							onClick={toggleSidebar}
							className={`btn btn-link p-2 text-decoration-none shadow-none ${darkMode ? 'text-light-emphasis hover-bg-dark-subtle' : 'text-secondary hover-bg-light-subtle'} rounded-circle`}
							style={{ transition: 'all 0.2s', width: '38px', height: '38px' }}
							title="Collapse Sidebar"
						>
							<i className="bi bi-chevron-left fs-5"></i>
						</button>
					)}
				</div>

				{!isMobile && isCollapsed && (
					<div className="mt-2">
						<button
							onClick={toggleSidebar}
							className={`btn btn-link p-0 w-100 text-decoration-none shadow-none ${darkMode ? 'text-light-emphasis hover-bg-dark-subtle' : 'text-secondary hover-bg-light-subtle'} rounded-3`}
							style={{ height: '38px' }}
							title="Expand Sidebar"
						>
							<i className="bi bi-chevron-right fs-5"></i>
						</button>
					</div>
				)}
			</div>

			{menuData.length > 0 ? (
				menuData.map((group, gIdx) => {
					const groupId = group.id || group.title || gIdx;
					// Default to OPEN — enterprise CRM pattern (Salesforce, HubSpot)
					const expanded = expandedGroups[groupId] === undefined ? true : expandedGroups[groupId];

					return (
						<div key={groupId} className={gIdx > 0 ? "mt-2 pt-2" : ""} style={gIdx > 0 ? { borderTop: '1px solid var(--bs-border-color)' } : undefined}>
							{(!isCollapsed || isMobile) ? (
								<div
									className={`d-flex align-items-center gap-2 fw-semibold mb-1 px-3 py-1 rounded-2 animate-fade-in cursor-pointer transition-all sidebar-group-header`}
									style={{
										userSelect: 'none',
										fontSize: '0.85rem',
										textTransform: 'uppercase',
										letterSpacing: '0.04em',
										color: expanded ? 'var(--bs-primary)' : 'var(--bs-secondary-color)',
									}}
									onClick={() => toggleGroup(groupId)}
								>
									{group.group_icon && (
										<i className={`${group.group_icon}`} style={{ fontSize: '0.75rem', opacity: 0.7 }}></i>
									)}
									<span className="text-nowrap flex-fill" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{group.group_title || group.title || group.label || group.name}
									</span>
									<i
										className="bi bi-chevron-right ms-auto"
										style={{
											fontSize: '0.6rem',
											transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
											transition: 'transform 0.25s ease',
											opacity: 0.5,
										}}
									></i>
								</div>
							) : (
								<div className="text-center my-1 px-2">
									{group.group_icon ? (
										<OverlayTrigger
											placement="right"
											overlay={renderTooltip(group.group_title || group.title || group.label || group.name)}
											delay={{ show: 50, hide: 50 }}
										>
											<div
												className="mx-auto d-flex align-items-center justify-content-center rounded-2"
												style={{
													width: 36,
													height: 28,
													opacity: 0.4,
													cursor: 'default'
												}}
											>
												<i className={`${group.group_icon}`} style={{ fontSize: '0.85rem' }}></i>
											</div>
										</OverlayTrigger>
									) : (
										<hr className="mx-2 opacity-15" />
									)}
								</div>
							)}

							<Collapse in={(!isCollapsed || isMobile) ? expanded : true}>
								<div>
									<div className="d-flex flex-column gap-0 w-100">
										{group.items.map((item, iIdx) => {
											const targetPath = (item.path || item.to || '').trim();
											const isPathMatch = (targetPath === '/dashboard' && location.pathname === '/') ||
												location.pathname === targetPath ||
												location.pathname.startsWith(targetPath + '/');

											const hasMoreSpecificMatch = isPathMatch && menuData.some(g =>
												Array.isArray(g.items) && g.items.some(otherItem => {
													const otherPath = (otherItem.path || otherItem.to || '').trim();
													return otherPath !== targetPath &&
														otherPath.length > targetPath.length &&
														(location.pathname === otherPath || location.pathname.startsWith(otherPath + '/') || location.pathname.startsWith(otherPath));
												})
											);

											const isActive = isPathMatch && !hasMoreSpecificMatch;





											const linkContent = (
												<Link
													to={targetPath}
													key={item.id || targetPath || iIdx}
													className={`nav-link d-flex align-items-center rounded-3 text-decoration-none transition-all ${isActive
														? 'bg-primary text-white active-menu-item shadow-sm w-100'
														: darkMode
															? 'text-light-emphasis hover-bg-dark-subtle'
															: 'text-body hover-bg-light-subtle'
														} ${!isMobile && isCollapsed ? 'justify-content-center p-0 mx-auto collapsed-nav-link' : 'px-3 py-2'}`}
													style={{
														transition: 'all 0.2s ease-in-out',
														fontSize: '0.92rem',
														marginBottom: '2px',
													}}
													onClick={isMobile ? closeMobileMenu : undefined}
												>
													<i className={`${item.icon} ${!isMobile && isCollapsed ? 'fs-5' : 'me-3'}`} style={{ width: !isMobile && isCollapsed ? 'auto' : '20px', fontSize: !isMobile && isCollapsed ? '1.2rem' : '1.05rem', textAlign: 'center' }} />
													{(!isCollapsed || isMobile) && <span className="fw-medium animate-fade-in text-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
												</Link>
											);

											if (!isMobile && isCollapsed) {
												return (
													<OverlayTrigger
														key={item.id || targetPath || iIdx}
														placement="right"
														overlay={renderTooltip(item.label)}
														delay={{ show: 50, hide: 50 }}
													>
														{linkContent}
													</OverlayTrigger>
												);
											}

											return linkContent;
										})}
									</div>
								</div>
							</Collapse>
						</div>
					)
				})
			) : (
				user?.role === 'admin' && (
					<div className="p-3 text-center animate-fade-in">
						<p className="small text-muted mb-2">Menu is empty</p>
						<Button as={Link} to="/settings/menus" variant="outline-primary" size="sm" className="rounded-pill w-100 shadow-sm">
							<i className="bi bi-gear me-2"></i>Setup Menu
						</Button>
					</div>
				)
			)}

			{
				isMobile && (
					<>
						<hr className={`my-3 ${darkMode ? 'border-secondary' : 'border-light-subtle'}`} />

						<div className="px-3 mb-3 d-flex flex-column gap-2">
							<div className="d-flex align-items-center justify-content-between">
								<span className={`small fw-bold ${darkMode ? 'text-light opacity-50' : 'text-muted'}`}>Language</span>
								<div className={`d-flex align-items-center p-1 rounded-pill border ${darkMode ? 'bg-secondary bg-opacity-10 border-secondary' : 'bg-body-tertiary border-light-subtle'}`} style={{ height: '34px' }}>
									<button
										type="button"
										className={`btn btn-sm rounded-pill px-2 py-0 border-0 fw-semibold transition-all ${i18n.language === 'th' ? 'bg-primary text-white shadow-sm' : 'text-body-secondary'}`}
										style={{ fontSize: '0.75rem' }}
										onClick={() => i18n.changeLanguage('th')}
									>
										TH
									</button>
									<button
										type="button"
										className={`btn btn-sm rounded-pill px-2 py-0 border-0 fw-semibold transition-all ${i18n.language.startsWith('en') ? 'bg-primary text-white shadow-sm' : 'text-body-secondary'}`}
										style={{ fontSize: '0.75rem' }}
										onClick={() => i18n.changeLanguage('en')}
									>
										EN
									</button>
								</div>
							</div>

							<button
								className={`btn btn-sm rounded-pill d-flex align-items-center gap-2 w-100 justify-content-center border ${darkMode
									? 'btn-outline-secondary text-light border-secondary'
									: 'bg-body-tertiary text-body border-light-subtle'
									}`}
								onClick={toggleDarkMode}
							>
								<i className={`bi ${darkMode ? 'bi-moon-stars-fill text-warning' : 'bi-sun-fill text-primary'}`}></i>
								<span className="small fw-medium">
									{darkMode ? 'Dark Mode' : 'Light Mode'}
								</span>
							</button>
						</div>
					</>
				)
			}

			{/* User Profile & Logout - pinned to bottom */}
			<div className={`mt-auto  w-100`}>
				<div
					className={`d-flex align-items-center ${(!isCollapsed || isMobile) ? 'justify-content-between rounded-3 p-2' : 'flex-column justify-content-center p-0 gap-2'} ${(!isCollapsed || isMobile) ? (darkMode ? 'bg-white bg-opacity-10' : 'bg-light') : ''} transition-all`}
				>
					<div className={`d-flex align-items-center ${(!isCollapsed || isMobile) ? 'gap-2 overflow-hidden' : ''}`}>
						{/* Avatar */}
						{user?.avatar_url ? (
							<img
								src={user.avatar_url}
								alt="User Avatar"
								className="rounded-3 flex-shrink-0"
								style={{ width: '36px', height: '36px', objectFit: 'cover' }}
								title={user?.first_name}
							/>
						) : (
							<div
								className="rounded-3 bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
								style={{ width: '36px', height: '36px', fontSize: '1.1rem', fontWeight: 'bold' }}
								title={user?.first_name}
							>
								{user?.first_name ? user.first_name.charAt(0).toUpperCase() : 'U'}
							</div>
						)}

						{(!isCollapsed || isMobile) && (
							<div className="d-flex flex-column overflow-hidden text-nowrap">
								<span className="fw-bold text-truncate text-body" style={{ fontSize: '0.85rem' }}>
									{user?.first_name} {user?.last_name}
								</span>
								<span className={`${darkMode ? 'text-light opacity-75' : 'text-muted'} text-truncate`} style={{ fontSize: '0.75rem' }}>
									{user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Staff'}
								</span>
							</div>
						)}
					</div>

					{(!isCollapsed || isMobile) ? (
						<Button
							variant="link"
							className={`p-1 shadow-none ${darkMode ? 'text-light opacity-75 hover-opacity-100' : 'text-secondary hover-text-danger'} text-decoration-none border-0 flex-shrink-0`}
							onClick={logout}
							title="ออกจากระบบ"
						>
							<i className="bi bi-box-arrow-right fs-5"></i>
						</Button>
					) : (
						<OverlayTrigger placement="right" overlay={<Tooltip>ออกจากระบบ</Tooltip>}>
							<Button
								variant="link"
								className={`p-1 shadow-none ${darkMode ? 'text-light opacity-75 hover-opacity-100' : 'text-secondary hover-text-danger'} text-decoration-none border-0`}
								onClick={logout}
							>
								<i className="bi bi-box-arrow-right fs-5"></i>
							</Button>
						</OverlayTrigger>
					)}
				</div>
			</div>

		</>
	);

	return (
		<div className="d-flex">
			{/* Mobile Header */}
			<div className={`d-lg-none position-fixed top-0 start-0 end-0 border-bottom px-3 d-flex align-items-center justify-content-between ${darkMode ? 'bg-body border-secondary' : 'bg-body border-light-subtle'}`}
				style={{ zIndex: 1040, height: '64px' }}>
				<div className="d-flex align-items-center gap-2">
					<button
						className={`btn btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center ${darkMode ? 'text-light hover-bg-dark-subtle' : 'text-body hover-bg-light-subtle'}`}
						style={{ width: '38px', height: '38px' }}
						onClick={toggleMobileMenu}
						aria-label="Toggle menu"
					>
						<i className="bi bi-list fs-3"></i>
					</button>
					<Link to="/" className="logo-container d-flex align-items-center">
						<img
							src={darkMode ? logoDark : logoWhite}
							alt="CRM Logo"
							style={{ maxHeight: '32px' }}
						/>
					</Link>
				</div>

				<div className="d-flex align-items-center gap-2">
					{/* Language Segment Pill */}
					<div className={`d-flex align-items-center p-1 rounded-pill border ${darkMode ? 'bg-secondary bg-opacity-10 border-secondary' : 'bg-body-tertiary border-light-subtle'}`} style={{ height: '34px' }}>
						<button
							type="button"
							className={`btn btn-sm rounded-pill px-2 py-0 border-0 fw-semibold transition-all ${i18n.language === 'th' ? 'bg-primary text-white shadow-sm' : 'text-body-secondary'}`}
							style={{ fontSize: '0.7rem' }}
							onClick={() => i18n.changeLanguage('th')}
						>
							TH
						</button>
						<button
							type="button"
							className={`btn btn-sm rounded-pill px-2 py-0 border-0 fw-semibold transition-all ${i18n.language.startsWith('en') ? 'bg-primary text-white shadow-sm' : 'text-body-secondary'}`}
							style={{ fontSize: '0.7rem' }}
							onClick={() => i18n.changeLanguage('en')}
						>
							EN
						</button>
					</div>

					{/* AI Assistant */}
					<button
						className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center transition-all ${darkMode
							? 'btn-outline-secondary text-info border-secondary'
							: 'bg-body-tertiary text-primary border border-light-subtle'
							}`}
						style={{ width: '36px', height: '36px' }}
						onClick={toggleAiPopup}
						title="AI Assistant"
					>
						<i className="bi bi-robot fs-6"></i>
					</button>

					<NotificationDropdown darkMode={darkMode} size={36} />
				</div>
			</div>

			{/* Desktop Sidebar */}
			<nav

				className={`sidebar border-end d-none d-lg-flex flex-column ${darkMode ? 'bg-white text-light border-secondary dark' : 'bg-white light'} ${isCollapsed ? 'sidebar-collapsed' : 'p-3'}`}
				style={{
					width: isCollapsed ? '80px' : '280px',
					padding: isCollapsed ? '20px 0' : undefined,
					minHeight: '100vh',
					transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
					position: 'sticky',
					top: 0,
					height: '100vh',
					overflowX: 'hidden',
					flexShrink: 0,
				}}
			>
				<SidebarContent />
			</nav>

			{/* Mobile Offcanvas Menu */}
			<Offcanvas
				show={showMobileMenu}
				onHide={closeMobileMenu}
				placement="start"
				className={darkMode ? 'bg-dark text-light border-end border-secondary' : 'bg-white'}
				style={{ width: '280px', transition: 'transform 0.3s ease-in-out' }}
			>
				<Offcanvas.Header closeButton closeVariant={darkMode ? 'white' : undefined} className="border-bottom border-light-subtle py-3">
				</Offcanvas.Header>
				<Offcanvas.Body className="p-0">
					<div className="p-3">
						<SidebarContent isMobile={true} />
					</div>
				</Offcanvas.Body>
			</Offcanvas>

			{/* Main Content */}
			<main
				className={`flex-fill ${isOmnichannel ? 'd-flex flex-column overflow-hidden' : 'overflow-y-auto'} overflow-x-hidden ${darkMode ? 'bg-dark text-light' : 'bg-light-subtle'} role-${user?.role}`}
				style={{
					height: isOmnichannel ? '100vh' : undefined,
					maxHeight: isOmnichannel ? '100vh' : undefined,
					minHeight: '100vh',
					paddingTop: '0',
					paddingLeft: '0',
					paddingRight: '0',
					transition: 'all 0.3s ease'
				}}
			>
				<div className="d-lg-none flex-shrink-0" style={{ height: '70px' }}></div>

				{/* Desktop Topbar */}
				<div className="d-none d-lg-block flex-shrink-0">
					<AppNavbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} toggleAiPopup={toggleAiPopup} />
				</div>

				<div
					className={isOmnichannel ? 'p-0 flex-grow-1 overflow-hidden d-flex flex-column' : 'p-3'}
					style={isOmnichannel ? { minHeight: 0 } : undefined}
				>
					<NavGuard permissions={permissions} userRole={user?.role} />
					<Outlet />
				</div>
				<AiBriefingPopup show={showAiPopup} onHide={() => setShowAiPopup(false)} />
			</main>
		</div>
	);
};

export default Layout;
